import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, TableShell, Field, Drawer } from "@/components/ui-kit";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const empty = {
  name: "", email: "", phone: "", license_number: "", license_expiry: "", status: "active",
  license_state: "", dob: "", hire_date: "", termination_date: "",
};
const STATUS = [
  { value: "active", label: "Active" },
  { value: "off_duty", label: "Off Duty" },
  { value: "suspended", label: "Suspended" },
];
// US state and territory 2-letter codes, for the MnDOT STS driver roster's "State of Issue" column.
const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID", "IL", "IN", "IA",
  "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM",
  "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA",
  "WV", "WI", "WY",
].map((code) => ({ value: code, label: code }));
const fmtDeletedDate = (iso) => (iso ? String(iso).slice(0, 10) : "");

// The STS driver roster needs license state, date of birth and hire date on top of the fields
// this form already required. Tell the person saving the record right away, at the moment of
// hiring, instead of only surfacing it later on the Compliance page.
const STS_DRIVER_FIELDS = [
  { key: "license_state", label: "license state" },
  { key: "dob", label: "date of birth" },
  { key: "hire_date", label: "hire date" },
];
function stsDriverToast(driver, noticeDate) {
  const missing = STS_DRIVER_FIELDS.filter((f) => !driver?.[f.key]).map((f) => f.label);
  const suffix = noticeDate ? `, MnDOT notice due ${noticeDate}` : "";
  if (missing.length === 0) toast.success(`Added to the STS driver list${suffix}`);
  else toast.success(`Saved, but the STS list needs: ${missing.join(", ")}${suffix}`);
}

// MnDOT gives an STS provider ten days from the change to send written notice (contract M).
// HichPlan opens that countdown the moment a hire or a termination-date change is saved, so
// tell the person saving the record the due date right away instead of only on Compliance.
function mndotNoticeDueDate() {
  const due = new Date();
  due.setDate(due.getDate() + 10);
  return due.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

export default function Drivers() {
  const { user } = useAuth();
  const isAdmin = user?.role === "agency_admin";
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const origTermination = React.useRef(null); // termination_date the drawer was opened with
  const [editingId, setEditingId] = useState(null);
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginFor, setLoginFor] = useState(null);
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [showDeleted, setShowDeleted] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  const load = () => api.get(`/drivers${showDeleted ? "?include_deleted=true" : ""}`).then((r) => setRows(r.data));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [showDeleted]);

  // Deep link from the Compliance "DOT inspection ready" panel: /app/drivers?edit=<id> opens
  // that driver straight into the edit dialog so a missing field can be fixed in one click.
  useEffect(() => {
    const editId = searchParams.get("edit");
    if (!editId || rows.length === 0) return;
    const d = rows.find((x) => x.id === editId);
    if (d) {
      setForm({ ...empty, ...d });
      origTermination.current = d.termination_date || null;
      setEditingId(d.id);
      setOpen(true);
    }
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows]);

  const restore = async (id) => {
    try {
      await api.post(`/drivers/${id}/restore`);
      toast.success("Restored");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    try {
      const p = {
        ...form,
        email: form.email || null,
        license_expiry: form.license_expiry || null,
        license_state: form.license_state || null,
        dob: form.dob || null,
        hire_date: form.hire_date || null,
        termination_date: form.termination_date || null,
      };
      // A new hire always opens an MnDOT add notice. On an edit, only a termination_date that
      // just went from empty to set (or set to empty) opens one (contract M); track the prior
      // value from the row this drawer was opened with, before this save overwrote it.
      const opensNotice = editingId
        ? Boolean(origTermination.current) !== Boolean(p.termination_date)
        : true;
      const { data } = editingId ? await api.put(`/drivers/${editingId}`, p) : await api.post("/drivers", p);
      stsDriverToast(data, opensNotice ? mndotNoticeDueDate() : null);
      setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const openLogin = (d) => {
    setLoginFor(d);
    setLoginForm({ email: d.email || "", password: "" });
    setLoginOpen(true);
  };

  const createLogin = async () => {
    try {
      await api.post("/team/users", {
        name: loginFor.name,
        email: loginForm.email,
        password: loginForm.password,
        role: "driver",
        driver_id: loginFor.id,
      });
      toast.success("Driver login created");
      setLoginOpen(false);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="drivers-page">
      <PageHeader
        subtitle="Fleet"
        title="Drivers"
        action={
          <div className="flex items-center gap-4">
            {isAdmin && (
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <input type="checkbox" checked={showDeleted} onChange={(e) => setShowDeleted(e.target.checked)} data-testid="drivers-show-deleted" />
                Show deleted
              </label>
            )}
            <button onClick={() => { setForm(empty); origTermination.current = null; setEditingId(null); setOpen(true); }} className="btn-primary" data-testid="add-driver-btn">+ Add Driver</button>
          </div>
        }
      />
      <TableShell columns={["Name", "License", "Contact", "Status", "Actions"]} empty={rows.length === 0 ? "No drivers yet." : null}>
        {rows.map((d) => {
          const isDeleted = !!d.deleted_at;
          return (
            <tr key={d.id} className={`tbl-row border-t border-slate-100 ${isDeleted ? "opacity-60" : ""}`}>
              <td className="px-4 py-2.5 font-medium">
                {d.name}
                {d.termination_date && (
                  <span className="ml-2 inline-block bg-rose-50 text-rose-700 border border-rose-200 rounded-full px-2 py-0.5 text-[10px] align-middle" data-testid={`driver-terminated-${d.id}`}>
                    Terminated {d.termination_date}
                  </span>
                )}
                {isDeleted && (
                  <span className="ml-2 inline-block bg-slate-100 text-slate-500 border border-slate-200 rounded-full px-2 py-0.5 text-[10px] align-middle">
                    deleted {fmtDeletedDate(d.deleted_at)}
                  </span>
                )}
              </td>
              <td className="px-4 py-2.5 font-mono text-xs">{d.license_number}<div className="text-slate-500 text-[11px]">exp {d.license_expiry || "-"}</div></td>
              <td className="px-4 py-2.5 text-slate-600">{d.email || "-"}<div className="text-slate-500 text-[11px]">{d.phone}</div></td>
              <td className="px-4 py-2.5 capitalize">{d.status.replace("_", " ")}</td>
              <td className="px-4 py-2.5 space-x-3">
                {isDeleted ? (
                  <button className="text-emerald-700 hover:underline text-xs" onClick={() => restore(d.id)} data-testid={`restore-${d.id}`}>Restore</button>
                ) : (
                  <>
                    <Link className="text-[#005E82] hover:underline text-xs" to={`/app/drivers/${d.id}/training`} data-testid={`driver-training-${d.id}`}>Training</Link>
                    <button className="text-[#005E82] hover:underline text-xs" onClick={() => openLogin(d)} data-testid={`driver-login-${d.id}`}>Create login</button>
                    <button className="text-[#005E82] hover:underline text-xs" onClick={() => { setForm({ ...empty, ...d }); origTermination.current = d.termination_date || null; setEditingId(d.id); setOpen(true); }}>Edit</button>
                    <button className="text-rose-600 hover:underline text-xs" onClick={async () => { if (confirm("Delete this driver? This can be restored by an admin from Show deleted.")) { await api.delete(`/drivers/${d.id}`); load(); } }}>Delete</button>
                  </>
                )}
              </td>
            </tr>
          );
        })}
      </TableShell>

      <Drawer open={open} onClose={() => setOpen(false)} title={editingId ? "Edit Driver" : "New Driver"} footer={<>
        <button onClick={() => setOpen(false)} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
        <button onClick={save} className="btn-primary" data-testid="save-driver-btn">Save</button>
      </>}>
        <div className="space-y-4">
          <Field label="Full name" testId="d-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          <Field label="Email" testId="d-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          <Field label="Phone" testId="d-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
          <div className="grid grid-cols-2 gap-3">
            <Field label="License #" testId="d-license" value={form.license_number} onChange={(e) => setForm({ ...form, license_number: e.target.value })} />
            <Field label="Expiry" testId="d-expiry" type="date" value={form.license_expiry} onChange={(e) => setForm({ ...form, license_expiry: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="License state" testId="d-license-state" value={form.license_state} onChange={(e) => setForm({ ...form, license_state: e.target.value })} options={[{ value: "", label: "Select" }, ...US_STATES]} />
            <Field label="Date of birth" testId="d-dob" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Hire date" testId="d-hire-date" type="date" value={form.hire_date} onChange={(e) => setForm({ ...form, hire_date: e.target.value })} />
            <Field label="Termination date" testId="d-termination-date" type="date" value={form.termination_date} onChange={(e) => setForm({ ...form, termination_date: e.target.value })} />
          </div>
          <Field label="Status" testId="d-status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} options={STATUS} />
        </div>
      </Drawer>

      <Drawer open={loginOpen} onClose={() => setLoginOpen(false)} title={`Create login for ${loginFor?.name || ""}`} footer={<>
        <button onClick={() => setLoginOpen(false)} className="text-sm px-3 py-2 rounded-md border border-slate-200">Cancel</button>
        <button onClick={createLogin} className="btn-primary" data-testid="save-driver-login-btn">Create login</button>
      </>}>
        <div className="space-y-4">
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-xs text-slate-600">
            The driver will sign in at the same URL. They'll be routed to a mobile-friendly portal showing only their assigned rides.
          </div>
          <Field label="Email" testId="dl-email" type="email" value={loginForm.email} onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })} />
          <Field label="Password" testId="dl-password" type="password" value={loginForm.password} onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })} />
        </div>
      </Drawer>
    </div>
  );
}
